import React, { useMemo } from "react"

import { useAgentMutationsAll } from "@/agents/agent-mutations"
import { useAgentsList } from "@/agents/catalog"
import { useMetricsList } from "@/metrics/catalog"
import { usePersonasList } from "@/persona"
import { useScheduledRunsListTransport } from "@/runs/scheduled"
import { useOrganizationTags } from "@/tags"
import { useTemplatesAll } from "@/templates/catalog"
import { useTestSetsList } from "@/test-set/catalog"
import { useListTestCasesTransport } from "@/test-set/transport"
import { ColorSwatch, Skeleton } from "@coval/ui"

import { formatMetadataFilter } from "@/lib/metadataFilters"
import {
  formatMetricValueFilterLabel,
  getMetricValueFilterKey,
} from "@/lib/metricValueFilters"
import { EditableFilterChip } from "@/components/filters/EditableFilterChip"
import { formatDateRangeLabel } from "@/utils/date/dateRangeLabel"

import { useRunsFilter } from "../contexts/RunsFilterContext"
import type { FilterSection } from "../types/filters"
import {
  createMetricReferenceResolver,
  findMetricRecordForFilterValue,
} from "../utils/runsTableState"

interface RunsFilterChipsProps {
  onEditFilter?: (section: FilterSection) => void
  selectedAgentId?: string | null
  /** Disable catalog lookups when the page scope is not ready. */
  filterEnabled?: boolean
}

export const RunsFilterChips: React.FC<RunsFilterChipsProps> = ({
  filterEnabled,
  onEditFilter,
  selectedAgentId,
}) => {
  const isDeferred = filterEnabled === false
  const {
    dateRange,
    setDateRange,
    statusFilters,
    templateFilters,
    personaFilters,
    agentFilters,
    metricFilters,
    metricValueFilters,
    tagFilters,
    mutationFilters,
    testSetFilters,
    testCaseFilters,
    metadataFilters,
    setMetadataFilters,
    scheduledJobFilters,
    hasActiveFilters,
    setStatusFilters,
    setTemplateFilters,
    setPersonaFilters,
    setAgentFilters,
    clearUrlAgentFilter,
    setMetricFilters,
    removeMetricValueFilter,
    removeTagFilter,
    setMutationFilters,
    setTestSetFilters,
    setTestCaseFilters,
    setScheduledJobFilters,
  } = useRunsFilter()

  // Defensive: ensure all filters are arrays (handles corrupted localStorage)
  const safeTagFilters = Array.isArray(tagFilters) ? tagFilters : []
  const safeStatusFilters = Array.isArray(statusFilters) ? statusFilters : []
  const safeTemplateFilters = Array.isArray(templateFilters)
    ? templateFilters
    : []
  const safePersonaFilters = Array.isArray(personaFilters) ? personaFilters : []
  const safeAgentFilters = Array.isArray(agentFilters) ? agentFilters : []
  const safeMetricFilters = useMemo(
    () => (Array.isArray(metricFilters) ? metricFilters : []),
    [metricFilters],
  )
  const safeMetricValueFilters = Array.isArray(metricValueFilters)
    ? metricValueFilters
    : []
  const safeMutationFilters = Array.isArray(mutationFilters)
    ? mutationFilters
    : []
  const safeTestSetFilters = Array.isArray(testSetFilters) ? testSetFilters : []
  const safeTestCaseFilters = Array.isArray(testCaseFilters)
    ? testCaseFilters
    : []
  const safeScheduledJobFilters = Array.isArray(scheduledJobFilters)
    ? scheduledJobFilters
    : []

  // Catalog lookups share page readiness but do not wait for table results.
  const { data: templatesData } = useTemplatesAll({ enabled: filterEnabled })
  const { data: personas } = usePersonasList({ enabled: filterEnabled })
  const { data: metricsData, metricIdAliases } = useMetricsList({
    enabled: filterEnabled,
    referencedMetricIds: safeMetricFilters,
  })
  const { tagColorMap } = useOrganizationTags({ enabled: filterEnabled })
  const { data: agents } = useAgentsList({ enabled: filterEnabled })
  const { data: testSets } = useTestSetsList({ enabled: filterEnabled })
  const { data: testCasesData } = useListTestCasesTransport({
    enabled: filterEnabled !== false && safeTestCaseFilters.length > 0,
  })
  const { data: scheduledRuns } = useScheduledRunsListTransport({
    enabled: filterEnabled !== false && safeScheduledJobFilters.length > 0,
  })
  const scheduledRunItems = scheduledRuns?.scheduled_runs

  // Fetch all mutations in a single call (avoids N separate API calls)
  const { data: allMutations } = useAgentMutationsAll({
    enabled: filterEnabled,
  })

  // Build agent ID to display name map
  const agentNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (agents) {
      agents.forEach((agent) => {
        map[agent.id] = agent.display_name || agent.id
      })
    }
    return map
  }, [agents])

  // Build mutation ID to display name map
  const mutationNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (allMutations) {
      allMutations.forEach((mutation) => {
        map[mutation.id] = mutation.display_name
      })
    }
    return map
  }, [allMutations])

  // Build test set ID to display name map
  const testSetNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (testSets) {
      testSets.forEach((testSet) => {
        map[testSet.id] = testSet.display_name || testSet.id
      })
    }
    return map
  }, [testSets])

  // Build test case ID to display name map
  const testCaseNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (testCasesData) {
      testCasesData.forEach((tc) => {
        const truncatedInput =
          tc.input_str.length > 40
            ? `${tc.input_str.slice(0, 40)}...`
            : tc.input_str
        map[tc.id] = tc.description || truncatedInput
      })
    }
    return map
  }, [testCasesData])

  // Build scheduled job ID to display name map
  const scheduledJobNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (scheduledRunItems) {
      scheduledRunItems.forEach((job) => {
        map[job.id] = job.display_name || job.id
      })
    }
    return map
  }, [scheduledRunItems])

  const templates = templatesData?.templates || []
  const metrics = useMemo(
    () =>
      metricsData
        ? Object.values(
            metricsData as Record<
              string,
              { id: string; metric_name: string; metric_display_name?: string }
            >,
          )
        : [],
    [metricsData],
  )
  const metricsById = useMemo(
    () => Object.fromEntries(metrics.map((metric) => [metric.id, metric])),
    [metrics],
  )
  const resolveMetricId = useMemo(
    () => createMetricReferenceResolver(metricsById, metricIdAliases),
    [metricsById, metricIdAliases],
  )
  const metricFilterChipIds = useMemo(() => {
    return Array.from(new Set(safeMetricFilters.map(resolveMetricId)))
  }, [safeMetricFilters, resolveMetricId])

  if (!hasActiveFilters && !selectedAgentId) {
    return null
  }

  const chips: Array<{
    id: string
    label?: string
    /** Overrides the section's default property label. */
    property?: string
    /** Overrides the default "is"; `null` omits the operator word. */
    operator?: string | null
    onRemove: () => void
    tagColor?: string | null
    isSkeleton?: boolean
    editable?: boolean
  }> = []

  // Created date chip
  const dateLabel = formatDateRangeLabel(dateRange, { style: "connector" })
  if (dateLabel) {
    chips.push({
      id: "date-range",
      label: dateLabel,
      onRemove: () => setDateRange({ start: null, end: null }),
    })
  }

  // Tag filter chips
  safeTagFilters.forEach((tag) => {
    chips.push({
      id: `tag-${tag}`,
      label: tag,
      onRemove: () => removeTagFilter(tag),
      tagColor: tagColorMap[tag],
    })
  })

  // Status chips
  safeStatusFilters.forEach((status) => {
    chips.push({
      id: `status-${status}`,
      label: status.toLowerCase(),
      onRemove: () =>
        setStatusFilters(safeStatusFilters.filter((s) => s !== status)),
    })
  })

  // Template chips
  safeTemplateFilters.forEach((templateName) => {
    const template = templates.find((t) => t.display_name === templateName)
    chips.push({
      id: `template-${templateName}`,
      label: template?.display_name || templateName,
      onRemove: () =>
        setTemplateFilters(
          safeTemplateFilters.filter((t) => t !== templateName),
        ),
    })
  })

  // Persona chips
  safePersonaFilters.forEach((personaId) => {
    const persona = personas?.find((p) => p.id === personaId)
    chips.push({
      id: `persona-${personaId}`,
      label: persona?.display_name || personaId,
      onRemove: () =>
        setPersonaFilters(safePersonaFilters.filter((p) => p !== personaId)),
      isSkeleton: isDeferred && !personas,
    })
  })

  // Agent chips
  const visibleAgentFilters = selectedAgentId
    ? [selectedAgentId]
    : safeAgentFilters
  visibleAgentFilters.forEach((agentId) => {
    chips.push({
      id: `agent-${agentId}`,
      label: agentNameMap[agentId] || agentId,
      onRemove: selectedAgentId
        ? clearUrlAgentFilter
        : () => setAgentFilters(safeAgentFilters.filter((a) => a !== agentId)),
      isSkeleton: isDeferred && !agents,
      editable: !selectedAgentId,
    })
  })

  // Metric chips
  metricFilterChipIds.forEach((metricFilter) => {
    const metric =
      findMetricRecordForFilterValue(metricFilter, metrics) ??
      metricsById[metricFilter]
    chips.push({
      id: `metric-${metricFilter}`,
      property: "Has Metric",
      operator: null,
      label: metric?.metric_display_name || metric?.metric_name || metricFilter,
      onRemove: () => {
        // An "Any value" condition on the same metric has no chip of its own
        // (see below), so removing this chip clears it too.
        safeMetricValueFilters
          .filter(
            (filter) =>
              filter.operator === "exists" &&
              resolveMetricId(filter.metricName) === metricFilter,
          )
          .forEach((filter) => removeMetricValueFilter(filter))
        setMetricFilters(
          safeMetricFilters.filter((candidateFilter) => {
            return resolveMetricId(candidateFilter) !== metricFilter
          }),
        )
      },
      isSkeleton: isDeferred && !metricsData,
    })
  })

  const presenceMetricIds = new Set(metricFilterChipIds)
  safeMetricValueFilters.forEach((filter) => {
    const metricId = filter.metricName
    if (
      filter.operator === "exists" &&
      presenceMetricIds.has(resolveMetricId(metricId))
    ) {
      return
    }
    const metric = metrics.find((m) => m.id === metricId)
    const metricLabel =
      metric?.metric_display_name || metric?.metric_name || metricId
    const isExistence = filter.operator === "exists"
    chips.push({
      id: `metric-value-${getMetricValueFilterKey(filter)}`,
      property: isExistence ? "Has Metric" : undefined,
      operator: null,
      label: isExistence
        ? metricLabel
        : formatMetricValueFilterLabel(filter, metricLabel),
      onRemove: () => removeMetricValueFilter(filter),
    })
  })

  // Mutation chips
  safeMutationFilters.forEach((mutationId) => {
    chips.push({
      id: `mutation-${mutationId}`,
      label: mutationNameMap[mutationId] || mutationId,
      onRemove: () =>
        setMutationFilters(safeMutationFilters.filter((m) => m !== mutationId)),
      isSkeleton: isDeferred && !allMutations,
    })
  })

  // Test set chips
  safeTestSetFilters.forEach((testSetId) => {
    chips.push({
      id: `testSet-${testSetId}`,
      label: testSetNameMap[testSetId] || testSetId,
      onRemove: () =>
        setTestSetFilters(safeTestSetFilters.filter((t) => t !== testSetId)),
      isSkeleton: isDeferred && !testSets,
    })
  })

  // Test case chips
  safeTestCaseFilters.forEach((testCaseId) => {
    chips.push({
      id: `testCase-${testCaseId}`,
      label: testCaseNameMap[testCaseId] || testCaseId,
      onRemove: () =>
        setTestCaseFilters(safeTestCaseFilters.filter((t) => t !== testCaseId)),
      isSkeleton: isDeferred && !testCasesData,
    })
  })

  for (const [index, filter] of (metadataFilters ?? []).entries()) {
    chips.push({
      id: `metadata-${index}`,
      label: formatMetadataFilter(filter),
      onRemove: () =>
        setMetadataFilters(
          (metadataFilters ?? []).filter((_, i) => i !== index),
        ),
    })
  }

  // Scheduled job chips
  safeScheduledJobFilters.forEach((jobId) => {
    chips.push({
      id: `scheduledJob-${jobId}`,
      label: scheduledJobNameMap[jobId] || jobId,
      onRemove: () =>
        setScheduledJobFilters(
          safeScheduledJobFilters.filter((j) => j !== jobId),
        ),
    })
  })

  const properties: Record<string, { label: string; section: FilterSection }> =
    {
      date: { label: "Created", section: "date" },
      metadata: { label: "Metadata", section: "metadata" },
      tag: { label: "Tag", section: "tags" },
      status: { label: "Status", section: "status" },
      template: { label: "Template", section: "templates" },
      persona: { label: "Persona", section: "personas" },
      agent: { label: "Agent", section: "agents" },
      metric: { label: "Metric", section: "metrics" },
      mutation: { label: "Mutation", section: "agents" },
      testSet: { label: "Test Set", section: "testSets" },
      testCase: { label: "Test Case", section: "testCases" },
      scheduledJob: { label: "Schedule", section: "scheduledJobs" },
    }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {chips.map((chip) => {
        const property = properties[chip.id.split("-")[0]]
        if (chip.isSkeleton) {
          return (
            <Skeleton key={chip.id} className="h-control w-32 rounded-md" />
          )
        }
        return (
          <EditableFilterChip
            key={chip.id}
            property={chip.property ?? property.label}
            operator={chip.operator}
            value={chip.label ?? ""}
            leading={
              chip.tagColor ? <ColorSwatch color={chip.tagColor} /> : undefined
            }
            onEdit={
              onEditFilter && chip.editable !== false
                ? () => onEditFilter(property.section)
                : undefined
            }
            onRemove={chip.onRemove}
          />
        )
      })}
    </div>
  )
}
